import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBoostListings, expireBoost } from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiCalendar,
  FiTrendingUp,
  FiShoppingBag,
  FiUser,
  FiPackage,
  FiZap,
  FiDollarSign,
  FiArrowLeft,
  FiArrowRight,
  FiActivity,
  FiPercent,
  FiStar,
  FiMapPin,
  FiCornerDownRight,
  FiFileText
} from 'react-icons/fi';

function BoostListings({ admin }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    date_from: '',
    date_to: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    active_boosts: 0,
    expiring_soon: 0,
    total_revenue: 0,
    avg_duration: 0,  
    total_listings: 0,
    premium_packages: 0,
    standard_packages: 0

  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchBoostListings();
  }, [filters, pagination.page]);

  const fetchBoostListings = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await getBoostListings(params);
      if (response.success) {
        setListings(response.data.listings || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
        
        // Calculate stats
        calculateStats(response.data.listings || []);
      } else {
        setError(response.message || 'Failed to load boosted listings');
      }
    } catch (err) {
      setError('Failed to load boosted listings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (listingsData) => {
    const now = new Date();
    const newStats = {
      active_boosts: 0,
      expiring_soon: 0,
      total_revenue: 0,
      total_listings: listingsData.length,
      premium_packages: 0,
      standard_packages: 0,
      avg_duration: 0  // Initialize avg_duration here
    };
  
    listingsData.forEach(listing => {
      if (listing.boost_status === 'active') {
        newStats.active_boosts++;
        newStats.total_revenue += parseFloat(listing.package_price || 0);
        
        // Check if expiring soon (within 24 hours)
        const expiresAt = new Date(listing.expires_at);
        const timeDiff = expiresAt - now;
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        if (hoursDiff <= 24 && hoursDiff > 0) {
          newStats.expiring_soon++;
        }
        
        // Count package types
        if (listing.package_name?.toLowerCase().includes('premium')) {
          newStats.premium_packages++;
        } else {
          newStats.standard_packages++;
        }
      }
    });
  
    // Calculate average duration
    if (listingsData.length > 0) {
      const totalDays = listingsData.reduce((sum, listing) => sum + (listing.duration_days || 0), 0);
      newStats.avg_duration = totalDays / listingsData.length;
    }
  
    setStats(newStats);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExpireBoost = async (boostId, orderId, listingTitle) => {
    if (!window.confirm(`Are you sure you want to expire the boost for "${listingTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await expireBoost(orderId);
      if (response.success) {
        setSuccessMessage(`Boost for "${listingTitle}" expired successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchBoostListings();
      } else {
        throw new Error(response.message || 'Expire failed');
      }
    } catch (err) {
      setError('Failed to expire boost: ' + err.message);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status, expiresAt) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full flex items-center justify-center w-28";
    
    if (status === 'active') {
      const expiresDate = new Date(expiresAt);
      const now = new Date();
      const hoursDiff = (expiresDate - now) / (1000 * 60 * 60);
      
      if (hoursDiff <= 24 && hoursDiff > 0) {
        return (
          <span className={`${baseClasses} bg-yellow-900/30 text-yellow-400 border-yellow-800/50`}>
            <FiAlertCircle className="mr-1" />
            Expiring Soon
          </span>
        );
      }
      return (
        <span className={`${baseClasses} bg-green-900/30 text-green-400 border-green-800/50`}>
          <FiCheckCircle className="mr-1" />
          Active
        </span>
      );
    } else if (status === 'expired') {
      return (
        <span className={`${baseClasses} bg-red-900/30 text-red-400 border-red-800/50`}>
          <FiXCircle className="mr-1" />
          Expired
        </span>
      );
    } else if (status === 'cancelled') {
      return (
        <span className={`${baseClasses} bg-gray-700 text-gray-400 border-gray-600`}>
          <FiXCircle className="mr-1" />
          Cancelled
        </span>
      );
    }
    
    return (
      <span className={`${baseClasses} bg-gray-700 text-gray-400`}>
        <FiAlertCircle className="mr-1" />
        {status}
      </span>
    );
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    
    if (diffTime < 0) return 'Expired';
    
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours}h`;
    }
    return `${diffHours}h`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatListingPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      date_from: '',
      date_to: '',
      page: 1,
      limit: 20
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Loading Skeleton
  if (loading && listings.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-48"></div>
                    <div className="h-3 bg-gray-700 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-700 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Boosted</span> Listings
            </h1>
            <p className="text-gray-400 mt-1">Monitor active, expiring, and expired listing boosts</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchBoostListings}
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Active Boosts</p>
              <p className="text-2xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">
                {stats.active_boosts}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {stats.total_listings} total listings
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiZap className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Expiring Soon</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1 group-hover:text-yellow-300 transition-colors">
                {stats.expiring_soon}
              </p>
              <p className="text-sm text-gray-500 mt-1">Within 24 hours</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-yellow-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiClock className="text-yellow-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Boost Revenue</p>
              <p className="text-2xl font-bold text-[#ff660e] mt-1 group-hover:text-orange-400 transition-colors">
                {formatCurrency(stats.total_revenue)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {stats.avg_duration.toFixed(1)} days avg
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[#ff660e]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiDollarSign className="text-[#ff660e] text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Package Types</p>
              <div className="flex items-center space-x-3 mt-1">
                <div className="text-sm">
                  <span className="text-[#ff660e] font-bold">{stats.premium_packages}</span>
                  <span className="text-gray-500"> premium</span>
                </div>
                <div className="text-sm">
                  <span className="text-blue-400 font-bold">{stats.standard_packages}</span>
                  <span className="text-gray-500"> standard</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">Distribution</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiPackage className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by listing, user, store, or package..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Boost Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="expiring_soon">Expiring Soon</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Starts From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expires By</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchBoostListings}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Boosted Listings Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Listing & User Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Boost Package
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Boost Period
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status & Time Left
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {listings.map((listing) => (
                <tr key={listing.boost_id} className="hover:bg-gray-750 transition-colors duration-150 group">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium rounded">
                          #{listing.listing_id}
                        </div>
                        <span className="px-2 py-1 bg-blue-900/30 text-blue-400 text-xs font-medium rounded border border-blue-800/50">
                          {listing.listing_status}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-white truncate max-w-xs group-hover:text-[#ff660e] transition-colors">
                        {listing.listing_title}
                      </div>
                      <div className="text-lg font-bold text-green-400 mt-1">
                        {formatListingPrice(listing.listing_price)}
                      </div>
                      <div className="flex items-center space-x-2 mt-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                          {listing.user_first_name?.charAt(0)}{listing.user_last_name?.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white">
                            {listing.user_first_name} {listing.user_last_name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {listing.user_email}
                          </div>
                        </div>
                      </div>
                      {listing.store_name && (
                        <div className="text-xs text-gray-500 mt-2">
                          <FiMapPin className="inline mr-1" />
                          {listing.store_name}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="px-2 py-1 bg-[#ff660e]/20 text-[#ff660e] text-sm font-medium rounded border border-[#ff660e]/30 flex items-center">
                          <FiPackage className="mr-1" />
                          {listing.package_name}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Duration: {listing.duration_days} days
                      </div>
                      <div className="text-xl font-bold text-white">
                        {formatCurrency(listing.package_price)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Boost ID: {listing.boost_id}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-gray-400 mb-1">Starts</div>
                        <div className="flex items-center space-x-2">
                          <FiCalendar className="text-green-400" />
                          <div>
                            <div className="text-sm font-medium text-white">{formatDate(listing.starts_at)}</div>
                            <div className="text-xs text-gray-500">{formatTime(listing.starts_at)}</div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-400 mb-1">Expires</div>
                        <div className="flex items-center space-x-2">
                          <FiCalendar className="text-red-400" />
                          <div>
                            <div className="text-sm font-medium text-white">{formatDate(listing.expires_at)}</div>
                            <div className="text-xs text-gray-500">{formatTime(listing.expires_at)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        Created: {formatDate(listing.created_at)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      {getStatusBadge(listing.boost_status, listing.expires_at)}
                      
                      {listing.boost_status === 'active' && (
                        <>
                          <div className="text-sm font-bold text-white mt-2">
                            {getTimeRemaining(listing.expires_at)} remaining
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full bg-gradient-to-r from-green-500 to-[#ff660e]"
                              style={{
                                width: `${Math.max(5, Math.min(100, (new Date(listing.expires_at) - new Date()) / (listing.duration_days * 24 * 60 * 60 * 1000) * 100))}%`
                              }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500">
                            Progress: {Math.round((new Date(listing.expires_at) - new Date()) / (listing.duration_days * 24 * 60 * 60 * 1000) * 100)}%
                          </div>
                        </>
                      )}
                      
                      {listing.boost_status === 'expired' && (
                        <div className="text-sm text-gray-500">
                          Expired {formatDate(listing.expires_at)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2 min-w-[180px]">
                      {/* View Listing */}
                      <button
                        onClick={() => navigate(`/admin/listings/${listing.listing_id}`)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02]"
                      >
                        <FiEye className="mr-2" />
                        View Listing
                      </button>
                      
                      {/* View Order */}
                      <button
                        onClick={() => navigate(`/admin/boost/orders/${listing.order_id}`)}
                        className="px-3 py-1.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 hover:text-blue-300 rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02] border border-blue-800/50"
                      >
                        <FiFileText className="mr-2" />
                        View Order
                      </button>
                      
                      {/* Expire Boost (only for active boosts) */}
                      {listing.boost_status === 'active' && (
                        <button
                          onClick={() => handleExpireBoost(listing.boost_id, listing.order_id, listing.listing_title)}
                          className="px-3 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02] border border-red-800/50"
                        >
                          <FiXCircle className="mr-2" />
                          Expire Boost
                        </button>
                      )}
                      
                      {/* View User */}
                      <button
                        onClick={() => navigate(`/admin/users/${listing.user_id}`)}
                        className="px-3 py-1.5 bg-purple-900/30 hover:bg-purple-900/50 text-purple-400 hover:text-purple-300 rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02] border border-purple-800/50"
                      >
                        <FiUser className="mr-2" />
                        View User
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {listings.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-600 text-8xl mb-6">🚀</div>
            <h3 className="text-xl font-medium text-white mb-2">No boosted listings found</h3>
            <p className="text-gray-400 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium hover:scale-[1.02]"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-900 px-6 py-4 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-400">
                Showing <span className="font-semibold text-white">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold text-white">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold text-white">{pagination.total}</span> boosted listings
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  <FiArrowLeft className="mr-2" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${
                          pagination.page === pageNum
                            ? 'bg-[#ff660e] text-white shadow-lg'
                            : 'border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  Next
                  <FiArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default BoostListings;